import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import { MatDialog } from '@angular/material/dialog';

import { translate, translateObject } from '@jsverse/transloco';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { DatasourceManagerStore } from 'app/modules/dashboard/stores/datasource-manager.store';
import { AnalyticsService } from 'app/providers/analytics.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { Dashboard, DashboardService } from 'app/providers/bridge-api/dashboard.service';
import { Preferences, SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { SystemService } from '../../../../services/system.service';
import { NewDashboardData } from '../new-dashboard/new-dashboard.component';
import { ProjectDetailService } from '../../services/project-detail.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-dashboard-actions',
  templateUrl: './project-dashboard-actions.component.html',
  styleUrls: ['./project-dashboard-actions.component.scss'],
  standalone: false,
})
export class ProjectDashboardActionsComponent implements OnChanges, OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() project: Project;

  dashboards: Dashboard[];
  private preferences: Preferences;
  private session: Session;
  showNewDashboardModal = false;
  newDashboardModalData: NewDashboardData;
  lastSelectedItemSort: {
    active: string;
    direction: string;
  };
  private localStorageService = inject(LocalStorageService);
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  private readonly logger = inject(LoggerService);
  constructor(
    private alertService: AlertService,
    private dashboardService: DashboardService,
    private groupService: GroupService,
    public dialog: MatDialog,
    public dialogService: DialogService,
    private googleAnalyticsService: AnalyticsService,
    private projectService: ProjectService,
    private preferencesService: SemantriaPreferences,
    private projectDetailService: ProjectDetailService,
    private authService: AuthService,
    private systemService: SystemService
  ) {}

  async ngOnInit() {
    this.preferences = await this.preferencesService.getPreferences();
    this.session = await this.authService.getSession();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.project && changes.project.currentValue) {
      this.dashboards = this.project.dashboards;
    }
  }

  public gotoDashboard(dashboard: Dashboard, event = undefined) {
    if (event?.metaKey || event?.detail?.metaKey) {
      const url = `/groups/${this.groupService.getSelectedGroupId(true)}/projects/${
        dashboard.project_id
      }/dashboards/${dashboard.id}`;
      window.open(url, '_blank');
      return;
    }

    this.dashboardService.gotoDashboard(dashboard);
  }

  public onShowNewDashboardDialog(
    sort: {
      active: string;
      direction: string;
    },
    dashboardTemplate?: Dashboard
  ) {
    this.lastSelectedItemSort = sort;
    this.newDashboardModalData = {
      project: this.project,
      timezone: this.preferences.timezone,
      timezoneOffset: this.preferences.timezoneOffset,
      saveAsDashboard: dashboardTemplate,
    };
    this.showNewDashboardModal = true;
  }

  onNewDashboardModalClosed(result: Dashboard) {
    this.showNewDashboardModal = false;
    if (result) {
      if (!result.id) {
        result.project_id = this.project.id;
        const dashboard = this.dashboardService.createCachedDashboard(
          result,
          this.session.info2.user_id
        );
        this.dashboardService.gotoDashboard(dashboard);
        return;
      }

      this.projectService
        .touchProject(this.project.id)
        .then(() => this.logger.debug('Touched project'));
      this.googleAnalyticsService.sendEvent('create_dashboard', 'Projects');
      const lastSelectedItem = {
        itemId: result.id,
        projectId: this.project.id,
        index: 0,
        sortDir: this.lastSelectedItemSort.direction,
        sortProp: this.lastSelectedItemSort.active,
      };
      this.lastSelectedItemSort = null;

      this.localStorageService.setItem('lastProjectItemSelected', lastSelectedItem);
      this.dashboardService.gotoDashboard(result);
    }
  }

  public async showDeleteDashboardDialog(dashboard: Dashboard): Promise<void> {
    const translations = translateObject('project-dashboard-actions.delete-dashboard', {
      tooltip: {
        name: dashboard.title,
      },
      deleting: {
        name: dashboard.title,
      },
      title: {
        shortName: Utils.truncate(dashboard.title, 30),
      },
    });

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        titleTooltip: dashboard.title.length > 30 ? translations['tooltip'] : '',
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(translations['deleting']);
          try {
            await this.dashboardService.deleteDashboard(dashboard.id, this.project.id);
            this.projectService
              .touchProject(this.project.id)
              .then(() => this.logger.debug('Touched project'));
            const idx = this.dashboards.findIndex(function (d) {
              return d.id === dashboard.id;
            });

            if (idx !== -1) {
              this.dashboards.splice(idx, 1);
            }
            this.projectDetailService.projectRefreshNeeded();
            this.googleAnalyticsService.sendEvent('delete_dashboard', 'Projects');
          } catch (error) {
            this.googleAnalyticsService.sendEvent('delete_dashboard_failed', 'Projects');
            this.alertService.showErrorToast(translations['delete-failed'], error);
            return;
          }
        }
      });
  }

  async cloneDashboard(dashboard: Dashboard) {
    if (this.dashboards.find((d) => d.title === dashboard.title + '_Copy')) {
      this.alertService.showToast(
        translate('project-dashboard-actions.clone-name-in-use', { name: dashboard.title })
      );
      return;
    }

    try {
      let clonedDashboard: Dashboard = Utils.deepCloneWithNulls(dashboard);

      clonedDashboard.title = clonedDashboard.title + '_Copy';
      for (const widget of clonedDashboard.widgets) {
        widget.payload.comments = widget.payload.comments?.filter((c) => !c.smartSummaryId);
      }

      clonedDashboard = await this.dashboardService.createDashboard(
        clonedDashboard,
        this.project.id,
        clonedDashboard.dashboard_version
      );

      this.projectService
        .touchProject(this.project.id)
        .then(() => this.logger.debug('Touched project'));
      const cloned_idx = this.dashboards.findIndex(function (d) {
        return d.id === clonedDashboard.id;
      });
      if (cloned_idx !== -1) {
        // remove it from where it is so we can reposition it
        // this can happen due to the rtrelay event coming through first
        this.dashboards.splice(cloned_idx, 1);
      }

      if (!this.dashboards.some((d) => d.id === dashboard.id)) {
        this.dashboards.unshift(clonedDashboard);
      }

      const prevSelectedItem = this.localStorageService.getItem('lastProjectItemSelected');
      const newSelectedItem = {
        itemId: clonedDashboard.id,
        projectId: this.project.id,
        index: null,
        sortDir: prevSelectedItem.sortDir,
        sortProp: prevSelectedItem.sortProp,
      };
      this.localStorageService.setItem('lastProjectItemSelected', newSelectedItem);

      this.googleAnalyticsService.sendEvent('clone_dashboard', 'Projects');

      this.alertService.showToast(
        translate('project-dashboard-actions.clone-success', { name: clonedDashboard.title })
      );
      this.projectDetailService.projectRefreshNeeded();
    } catch (error) {
      this.alertService.showErrorToast(translate('project-dashboard-actions.clone-failed'), error);
    }
  }

  public get canShowNewDashboardDialog(): boolean {
    if (!this.project) {
      return false;
    }
    // find at least one collection that has an analysis, or is set to auto analyze and has documents
    return (
      -1 !==
      this.project.collections.findIndex(
        (c) => c.analysisCount > 0 || (c.isAutoAnalyze && c.documentCount > 0)
      )
    );
  }

  onCopyDashboardIdToClipboard(dashboard: Dashboard) {
    this.systemService.copyToClipboard(dashboard.id);
    this.alertService.showToast(translate('project-collection-actions.dashboard-id-copied'));
  }

  ngOnDestroy() {}
}
